/**
 * Definición de los menús de navegación (DEF-ADM-009 — dos niveles).
 *
 * Una sola fuente de verdad consumida por el sidebar de escritorio (PageBase)
 * y el de mobile (PageSidebarMobile), para no mantener el menú duplicado.
 *
 * Forma de cada entrada:
 *   { key, text, icon, route }                        -> ítem simple
 *   { key, text, icon, description, children: [...] } -> grupo con submenú
 * Cada child: { text, route }
 */

// Nivel 1 — Administración: datos globales, compartidos entre ciudades.
export const ADMIN_MENU = [
  {
    key: "dashboard-operativo",
    text: "Dashboard",
    icon: "ri-dashboard-line",
    route: "dashboard-operativo",
  },
  {
    key: "exchange-rate-list",
    text: "Tipos de cambio",
    icon: "ri-exchange-dollar-line",
    route: "exchange-rate-list",
  },
  {
    key: "geografia",
    text: "Geografía",
    icon: "ri-earth-line",
    description: "Países, Ciudades, Aeropuertos, Distritos",
    children: [
      { text: "Países", route: "geografia-country-list" },
      { text: "Ciudades", route: "geografia-city-list" },
      { text: "Aeropuertos", route: "geografia-airport-list" },
      { text: "Distritos", route: "ubigeo-departamentos" },
    ],
  },
  {
    key: "config-user-list",
    text: "Usuarios",
    icon: "ri-shield-user-line",
    route: "config-user-list",
  },
  {
    key: "config-passenger-list",
    text: "Pasajeros",
    icon: "ri-user-line",
    route: "config-passenger-list",
  },
  {
    key: "config-coupon-list",
    text: "Cupones",
    icon: "ri-coupon-3-line",
    route: "config-coupon-list",
  },
  {
    key: "travelbot",
    text: "Travelbot",
    icon: "ri-robot-2-line",
    description: "Black list",
    children: [{ text: "Black List", route: "travelbot-black-list" }],
  },
  {
    key: "config-ota-list",
    text: "OTAs",
    icon: "ri-flight-takeoff-line",
    route: "config-ota-list",
  },
  {
    key: "config-vehicle-type-list",
    text: "Tipos de Vehículo",
    icon: "ri-roadster-line",
    route: "config-vehicle-type-list",
  },
  {
    key: "config-category-list",
    text: "Categorías",
    icon: "ri-price-tag-3-line",
    route: "config-category-list",
  },
  {
    // DEF-ADM-022 — importación de viajes desde el Excel de una OTA. Va en
    // Administración porque un archivo trae viajes de varias ciudades.
    key: "imports",
    text: "Importar viajes",
    icon: "ri-file-upload-line",
    children: [{ text: "Despegar", route: "imports-despegar" }],
  },
  {
    // DEF-ADM-024 — procesos batch que lanza el operador. Va en Administración
    // porque la pantalla elige su propia ciudad, no usa la activa.
    key: "executions",
    text: "Ejecuciones",
    icon: "ri-play-circle-line",
    description: "Confirmaciones",
    children: [{ text: "Confirmaciones", route: "executions-confirmations" }],
  },
  {
    // DEF-ADM-025 — catálogo de plantillas de WhatsApp. Es configuración, no
    // ejecución, por eso va suelto y no dentro de Ejecuciones.
    key: "whatsapp-template-list",
    text: "Plantillas WhatsApp",
    icon: "ri-whatsapp-line",
    route: "whatsapp-template-list",
  },
  {
    // DEF-ADM-032 — Tesorería. Va en Administración y no en Ciudad porque el
    // reporte cruza las ciudades: la ciudad es un filtro, no el contexto.
    //
    // "Liquidaciones a proveedores" NO entra acá aunque hable de plata: es
    // nivel Ciudad y se queda en el grupo Proveedores. Son dos cosas opuestas
    // —lo que entra y lo que sale— y juntarlas fue justo lo que el DEF pidió
    // no hacer.
    key: "tesoreria",
    text: "Tesorería",
    icon: "ri-wallet-3-line",
    description: "Cobranzas",
    children: [{ text: "Cobranzas", route: "tesoreria-cobranzas" }],
  },
  {
    key: "audit-log",
    text: "Auditoría",
    icon: "ri-file-list-3-line",
    route: "audit-log",
  },
];

// Nivel 2 — Ciudad: datos que pertenecen a la ciudad activa.
export const CITY_MENU = [
  {
    key: "viajes-ride-list",
    text: "Viajes",
    icon: "ri-car-line",
    route: "viajes-ride-list",
  },
  {
    key: "tracking-fleet",
    text: "Tracking",
    icon: "ri-radar-line",
    route: "tracking-fleet",
  },
  {
    key: "tarifas",
    text: "Tarifas",
    icon: "ri-price-tag-3-line",
    description: "Tarifario, Precios Base, Listas",
    children: [
      { text: "Tarifario", route: "tarifas-tarifario" },
      { text: "Precios Base", route: "tarifas-base-price-list" },
      { text: "Listas de Precios", route: "tarifas-price-type-list" },
    ],
  },
  {
    key: "config-affiliate-list",
    text: "Afiliados",
    icon: "ri-hotel-line",
    route: "config-affiliate-list",
  },
  {
    key: "config-driver-list",
    text: "Conductores",
    icon: "ri-user-star-line",
    route: "config-driver-list",
  },
  {
    key: "config-vehicle-list",
    text: "Vehículos",
    icon: "ri-taxi-line",
    route: "config-vehicle-list",
  },
  {
    key: "providers",
    text: "Proveedores",
    icon: "ri-community-line",
    description: "Asociados, Transportistas, Compliance",
    children: [
      { text: "Proveedores", route: "providers-list" },
      { text: "Liquidaciones", route: "settlements-list" },
      { text: "Compliance", route: "providers-compliance" },
    ],
  },
  {
    key: "config-document-type-list",
    text: "Tipos de documento",
    icon: "ri-file-list-3-line",
    route: "config-document-type-list",
  },
  {
    key: "fleet",
    text: "Flota",
    icon: "ri-truck-line",
    description: "Gastos, Lavados, Mantenimientos, Contratos",
    children: [
      { text: "Gastos", route: "fleet-expenses" },
      { text: "Lavados", route: "fleet-washes" },
      { text: "Mantenimientos", route: "fleet-maintenances" },
      { text: "Contratos", route: "fleet-rentals" },
    ],
  },
];

// Ruta de aterrizaje al entrar a cada nivel.
export const ADMIN_HOME_ROUTE = "geografia-city-list";
export const CITY_HOME_ROUTE = "viajes-ride-list";
